/**
 * Обёртка над API мини-аппов Telegram.
 *
 * Приложение должно открываться и в обычном браузере — иначе его невозможно
 * разрабатывать и отлаживать. Поэтому все обращения к Telegram идут через эти
 * функции, а при отсутствии вебвью они просто ничего не делают.
 */

import { parseReferrerTelegramId } from "@koshkodrop/shared";

import { api } from "./api";

/** granted — разрешил, denied — отказал в этой сессии. */
const WRITE_ACCESS_KEY = "koshkodrop.write_access";
const PENDING_WELCOME_KEY = "koshkodrop.pending_welcome";

let writeAccessAskedThisLoad = false;

interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: {
    start_param?: string;
    user?: { id: number; first_name: string; allows_write_to_pm?: boolean };
  };
  colorScheme: "light" | "dark";
  viewportStableHeight: number;
  isExpanded: boolean;
  ready: () => void;
  expand: () => void;
  close: () => void;
  disableVerticalSwipes?: () => void;
  requestFullscreen?: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  onEvent: (event: string, handler: () => void) => void;
  offEvent: (event: string, handler: () => void) => void;
  HapticFeedback?: {
    impactOccurred: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };
  openTelegramLink?: (url: string) => void;
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
  openInvoice?: (
    url: string,
    callback?: (status: "paid" | "cancelled" | "failed" | "pending") => void,
  ) => void;
  /** Bot API 6.9+: нативный запрос «разрешить боту писать». */
  requestWriteAccess?: (callback?: (granted: boolean) => void) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export const isInsideTelegram = (): boolean => Boolean(getWebApp()?.initData);

/** Подготавливает вебвью: разворачивает на весь экран и убирает свайп-закрытие. */
export function initTelegram(): void {
  const app = getWebApp();
  if (!app) return;

  app.ready();
  app.expand();

  // Без этого свайп вниз по игровому полю закрывает мини-апп прямо во время
  // раунда — игрок теряет ставку из-за жеста прокрутки
  app.disableVerticalSwipes?.();

  app.setHeaderColor?.("#07061a");
  app.setBackgroundColor?.("#07061a");
}

/** start_param из ссылки мини-аппа (?startapp=…). */
export function getStartParam(): string | null {
  const value = getWebApp()?.initDataUnsafe?.start_param?.trim();
  return value || null;
}

/** Разрешил ли игрок боту писать в личку. */
export function hasBotWriteAccess(): boolean {
  return getWebApp()?.initDataUnsafe?.user?.allows_write_to_pm === true;
}

/**
 * При входе по реферальной ссылке показывает нативный запрос Telegram
 * «разрешить боту писать» прямо в панели мини-аппа.
 *
 * Вызывается после входа, когда WebApp уже полностью готов. Раньше флаг
 * ставился до показа попапа — из-за этого повторный запуск в той же сессии
 * молча пропускал запрос.
 */
export function maybeRequestReferralWriteAccess(): void {
  const app = getWebApp();
  if (!app?.requestWriteAccess) return;
  if (writeAccessAskedThisLoad) return;
  // Старый ключ ставился до показа попапа и навсегда блокировал запрос в сессии
  if (sessionStorage.getItem("koshkodrop.write_access_requested") === "1") {
    sessionStorage.removeItem("koshkodrop.write_access_requested");
  }
  if (!parseReferrerTelegramId(getStartParam())) return;

  if (hasBotWriteAccess()) {
    sessionStorage.setItem(WRITE_ACCESS_KEY, "granted");
    return;
  }

  const previous = sessionStorage.getItem(WRITE_ACCESS_KEY);
  if (previous === "granted") return;
  // Отказ в этой сессии — не дёргаем снова до следующего открытия мини-аппа
  if (previous === "denied") return;

  writeAccessAskedThisLoad = true;

  // Небольшая задержка: если вызвать сразу на ready(), Telegram иногда не показывает попап
  window.setTimeout(() => {
    app.requestWriteAccess?.((granted) => {
      sessionStorage.setItem(WRITE_ACCESS_KEY, granted ? "granted" : "denied");
      if (granted) {
        sessionStorage.setItem(PENDING_WELCOME_KEY, "1");
        void api.referralsWelcome().catch(() => {});
      }
    });
  }, 400);
}

/** Нужно отправить приветствие в боте после выдачи разрешения. */
export function hasPendingWelcome(): boolean {
  return sessionStorage.getItem(PENDING_WELCOME_KEY) === "1";
}

export function clearPendingWelcome(): void {
  sessionStorage.removeItem(PENDING_WELCOME_KEY);
}

/** initData для входа. Пустая строка означает запуск вне Telegram. */
export function getInitData(): string {
  return getWebApp()?.initData ?? "";
}

type HapticStyle = "light" | "medium" | "heavy" | "soft" | "rigid";

export const haptic = {
  tap(style: HapticStyle = "light"): void {
    getWebApp()?.HapticFeedback?.impactOccurred(style);
  },
  success(): void {
    getWebApp()?.HapticFeedback?.notificationOccurred("success");
  },
  error(): void {
    getWebApp()?.HapticFeedback?.notificationOccurred("error");
  },
  select(): void {
    getWebApp()?.HapticFeedback?.selectionChanged();
  },
};

/** Нативный счёт Telegram Stars внутри мини-аппа. */
export function openStarsInvoice(
  url: string,
): Promise<"paid" | "cancelled" | "failed" | "pending"> {
  const app = getWebApp();
  if (!app?.openInvoice) {
    window.open(url, "_blank");
    return Promise.resolve("pending");
  }

  return new Promise((resolve) => {
    app.openInvoice!(url, (status) => resolve(status));
  });
}

/** Счёт CryptoBot: открывается их мини-аппом, если ссылка telegram. */
export function openChannel(url: string): void {
  const app = getWebApp();
  if (app?.openTelegramLink && url.startsWith("https://t.me/")) {
    app.openTelegramLink(url);
    return;
  }
  window.open(url, "_blank");
}

export function openCryptoInvoice(url: string): void {
  const app = getWebApp();
  if (url.startsWith("https://t.me/") && app?.openTelegramLink) {
    app.openTelegramLink(url);
    return;
  }
  if (app?.openLink) {
    app.openLink(url);
    return;
  }
  window.open(url, "_blank");
}

/**
 * Высота видимой области вебвью. Отличается от innerHeight: в Telegram клавиатура
 * и системные панели меняют её, а вёрстка должна подстраиваться.
 */
export function useViewportHeight(): void {
  const app = getWebApp();
  const apply = () => {
    const height = app?.viewportStableHeight ?? window.innerHeight;
    document.documentElement.style.setProperty("--viewport-height", `${height}px`);
  };

  apply();

  if (app) {
    app.onEvent("viewportChanged", apply);
  } else {
    window.addEventListener("resize", apply);
  }
}
